/// Vínculo de trabalho de quem recebe comissão (ADR 0082, decisão 21). Salão-parceiro fica de fora
/// até o ToDo 13 desenhar a cota-parte.
export const EMPLOYMENT_BONDS = ['CLT', 'AUTONOMOUS', 'MEI_PJ', 'INFORMAL'] as const;
export type EmploymentBond = (typeof EMPLOYMENT_BONDS)[number];

export const employmentBondLabels: Readonly<Record<EmploymentBond, string>> = Object.freeze({
  CLT: 'CLT',
  AUTONOMOUS: 'Autônomo com RPA',
  MEI_PJ: 'MEI ou PJ com nota',
  INFORMAL: 'Sem contrato formal',
});

/// A conta do plano da despesa da comissão. Sem contrato formal não tem padrão: a empresa escolhe a
/// conta com o contador (ADR 0082, decisão 21).
export function commissionAccountCode(bond: EmploymentBond, offPayroll: boolean): string | null {
  if (bond === 'CLT') return offPayroll ? '4.1.7' : '4.1.3';
  if (bond === 'INFORMAL') return null;
  return '4.4.4';
}

/// O que oferece comissão, como é calculada e, no pacote, quando nasce (ADR 0082, decisões 1 e 24).
export const COMMISSION_ITEM_KINDS = ['SERVICE', 'PRODUCT', 'PACKAGE'] as const;
export type CommissionItemKind = (typeof COMMISSION_ITEM_KINDS)[number];
export const COMMISSION_RATE_TYPES = ['PERCENT', 'FIXED'] as const;
export type CommissionRateType = (typeof COMMISSION_RATE_TYPES)[number];
export const COMMISSION_PACKAGE_MODES = ['ON_SALE', 'PER_SESSION'] as const;
export type CommissionPackageMode = (typeof COMMISSION_PACKAGE_MODES)[number];

export const commissionItemKindLabels: Readonly<Record<CommissionItemKind, string>> = Object.freeze(
  { SERVICE: 'Serviço', PRODUCT: 'Produto', PACKAGE: 'Pacote' },
);
export const commissionPackageModeLabels: Readonly<Record<CommissionPackageMode, string>> =
  Object.freeze({ ON_SALE: 'Na venda', PER_SESSION: 'Por sessão realizada' });

/// O que é o vale e o que ele muda, no ícone de ajuda de Registrar vale (ADR 0082, decisões 24 e 26).
export const commissionAdvanceHelp =
  'O vale é dinheiro adiantado ao profissional por conta da comissão. Registrar aqui não mexe no caixa nem cria despesa: o Pix ou a retirada aparece no extrato e é classificado em 9.10 Adiantamentos a profissionais, para o custo não contar duas vezes no DRE. Na apuração do mês, o vale abate o que falta pagar ao profissional, e a despesa da comissão continua pelo valor cheio. Se o vale passar da comissão do mês, o saldo abate no mês seguinte. Para quem está na folha, confirme com o contador como lançar.';

/// Percentual em pontos-base (1250 = 12,5%); valor fixo em centavos por unidade vendida.
export interface CommissionRate {
  rateType: CommissionRateType;
  rateValue: number;
}

export interface CommissionWarning {
  title: string;
  paragraphs: readonly string[];
  note: string;
}

/// O aviso mostrado antes de confirmar a escolha. Informa e nunca impede (ADR 0082, decisão 23).
export const commissionWarnings: Readonly<Record<'OFF_PAYROLL' | 'INFORMAL', CommissionWarning>> =
  Object.freeze({
    OFF_PAYROLL: {
      title: 'Comissão de funcionário CLT fora da folha',
      paragraphs: [
        'A comissão integra o salário (CLT, art. 457, §1º), e um acordo entre as partes não muda isso (art. 9º). A empresa pode ser cobrada por diferenças de férias, 13º, FGTS e rescisão, além de INSS e possível responsabilização por sonegação.',
        'Prêmio por desempenho superior (art. 457, §§ 2º e 4º) e PLR (Lei 10.101/2000) são alternativas a avaliar. Fale com seu contador ou advogado.',
      ],
      note: 'A decisão é da empresa. O sistema registra quem escolheu, e a comissão aparece no DRE na conta 4.1.7.',
    },
    INFORMAL: {
      title: 'Profissional sem contrato formal',
      paragraphs: [
        'A Justiça pode reconhecer o vínculo de emprego se houver habitualidade, subordinação e pessoalidade (CLT, art. 3º), com encargos retroativos. Fale com seu contador ou advogado.',
      ],
      note: 'A decisão é da empresa. O sistema registra quem escolheu, e a comissão aparece no DRE na conta escolhida com o contador.',
    },
  });
